/* eslint-disable @typescript-eslint/no-explicit-any */
import { OutputTransformFn } from 'ngx-mask';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { isNil, omitBy } from 'lodash-es';

export const isBitMaskFlagSet = (
  bitMaskValue: number,
  valueToTest: number
): boolean => (bitMaskValue & valueToTest) == valueToTest;

export const removeNilProperties = (
  value: Record<string, any>
): Record<string, any> => {
  return omitBy(value, value => isNil(value) || value === '');
};

export const getNamesFromFullName = (
  fullName: string | null | undefined
): { lastName: string; firstName: string } => {
  if (!fullName) return { lastName: '', firstName: '' };
  const [lastName, ...rest] = fullName.split(' ');
  const firstName = rest.join(' ');

  return { lastName, firstName };
};

export const isContinuousSequenceOfNumber = (input: number[]): boolean => {
  if (input.length < 2) {
    return true;
  }

  for (let i = 0; i < input.length - 1; i++) {
    if (input[i + 1] - input[i] !== 1) {
      return false;
    }
  }

  return true;
};

export const pickFirstAndLast = <T>(input: T[]): T[] => {
  if (input.length <= 2) {
    return input;
  }

  return [input.at(0)!, input.at(-1)!];
};

export function extractFromRequestData<T = unknown>(
  request: HttpRequest<any>,
  key: string
): {
  request: HttpRequest<any>;
  value: T | null;
} {
  const paramsValue = request.params.get(key);
  if (paramsValue) {
    return {
      request: request.clone({
        params: request.params.delete(key)
      }),
      value: JSON.parse(paramsValue)
    };
  }

  if (request.body instanceof FormData) {
    const bodyValue = request.body.get(key);
    if (bodyValue) {
      request.body.delete(key);
      return {
        request: request,
        value: bodyValue instanceof File ? bodyValue : JSON.parse(bodyValue)
      };
    }
  } else if (request.body instanceof URLSearchParams) {
    const bodyValue = request.body.get(key);
    if (bodyValue) {
      request.body.delete(key);
      return {
        request: request,
        value: JSON.parse(bodyValue)
      };
    }
  } else if (request.body) {
    const bodyValue = request.body[key];
    if (Object.hasOwn(request.body, key)) {
      delete request.body[key];
      return {
        request,
        value: JSON.parse(bodyValue)
      };
    }
  }

  return {
    request,
    value: null
  };
}

export function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return 'Il semble que vous ayez un souci de connexion';
    }

    if (error.status >= 500) {
      return 'Une erreur inconnue est survenue, veuillez réessayer plus tard.';
    }

    if (error.status >= 400 && error.status < 500) {
      if (error.error?.violations) return '';

      return error.error.detail || error.error.description || error.message;
    }

    return '';
  }

  return '';
}

export const taxPercentage = (
  rate: string | number | null | undefined
): number => {
  if (rate == null) return 0;
  return Number(rate) * 100;
};

export function getFullName(...names: (string | null | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

export function humanizeText(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

export const parseMaskToNumber: OutputTransformFn = (
  value: string | number | null | undefined
): number | null => {
  if (value == null || value === '') return null;
  return Number(String(value).replace(/\s/g, '').replace(',', '.'));
};

export const getFlagEmoji = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

export const formatLargeAmount = (
  amount: number | undefined,
  currency?: string,
  options: { returnFullOnly?: boolean; excludeCurrency?: boolean } = {}
): { formatted: string; full: string } | string => {
  if (!amount && amount !== 0) {
    const defaultValue = { formatted: '0', full: '0' };
    return options.returnFullOnly ? defaultValue.full : defaultValue;
  }

  const currencySymbol = currency || 'XOF';
  const fullAmountNumber = amount.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const fullAmount = options.excludeCurrency
    ? fullAmountNumber
    : `${fullAmountNumber} ${currencySymbol}`;

  if (Math.abs(amount) < 1_000_000) {
    return options.returnFullOnly
      ? fullAmount
      : { formatted: fullAmount, full: fullAmount };
  }

  const absAmount = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';

  const scales = [
    { threshold: 1e27, divisor: 1e27, suffix: 'Octillion' },
    { threshold: 1e24, divisor: 1e24, suffix: 'Septillion' },
    { threshold: 1e21, divisor: 1e21, suffix: 'Sextillion' },
    { threshold: 1e18, divisor: 1e18, suffix: 'Quintillion' },
    { threshold: 1e15, divisor: 1e15, suffix: 'Quadrillion' },
    { threshold: 1e12, divisor: 1e12, suffix: 'Billion' },
    { threshold: 1e9, divisor: 1e9, suffix: 'Mrd' },
    { threshold: 1e6, divisor: 1e6, suffix: 'M' },
    { threshold: 1e3, divisor: 1e3, suffix: 'K' }
  ];

  const scale = scales.find(s => absAmount >= s.threshold);

  if (scale) {
    const value = absAmount / scale.divisor;

    const formattedValue =
      value >= 1000
        ? value.toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
        : value.toFixed(2);

    const formatted = options.excludeCurrency
      ? `${sign}${formattedValue} ${scale.suffix}`
      : `${sign}${formattedValue} ${scale.suffix} ${currencySymbol}`;

    return options.returnFullOnly
      ? fullAmount
      : { formatted, full: fullAmount };
  }

  return options.returnFullOnly
    ? fullAmount
    : { formatted: fullAmount, full: fullAmount };
};

export function formatAmount(amount: number): string {
  return amount.toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}
